import React, { useState, useCallback, useMemo, useEffect } from 'react';
import withStyles from '@material-ui/core/styles/withStyles';
import CommonTabStyles from './TabsStyles';
import {
  useQueryWithStore,
  Loading,
  Error,
  useQuery,
  SelectInput,
  useNotify,
  SaveButton
} from 'react-admin';
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  Button,
  Tooltip
} from '@material-ui/core/';
import MailIcon from '@material-ui/icons/Mail';
import { uniq, uniqBy, difference, isEmpty, filter, find } from 'lodash';
import {
  filtersLookupsReporting,
  filtersLookupsEmail
} from './CommonTabLookups';
import { saveAs } from 'file-saver';
import DocumentApi from '../../../../core/apis/DocumentApi';
import { getIcError } from '../../../../core/providers/utils';
import ResourceUploadApi from '../../../../core/apis/ResourceUploadApi';
import PostalMailApi from '../../../../core/apis/PostalMailApi';
import Confirm from '../../../../core/components/forked/layout/Confirm';
import useNotifyServerError from '../../../../core/hooks/useNotifyServerError';
import { PendingIndicatorIcon } from '../../../../core/themes/icons/PendingIndicator';
import CustomSimpleForm from '../../../../core/components/form/CustomSimpleForm';

const limitToCall = 50;

const DeliveryTable = ({
  recipientsList,
  sendHardCopy,
  classes,
  resourceUploadId,
  handleOpenEmailModal,
  notificationMethod,
  handleDownloadAsExcel,
  isLoadingExcelDoc,
  hasExportPermission
}) => {
  const [recipientsListFiltered, setRecipientsListFilter] = useState(
    recipientsList
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [numSelectedRows, setNumSelectedRows] = useState(0);

  const [contactFilters, setContactFilters] = useState(
    notificationMethod === 'EMAIL' ? 'NON_ADMIN' : 'ALL'
  );

  //get contacts that are not Admin
  const nonAdminContacts = useMemo(() => {
    return recipientsList.filter(item => {
      return !item.admin;
    });
  }, [recipientsList]);

  // get contacts that are Admin
  const adminContacts = useMemo(() => {
    return recipientsList.filter(item => {
      return item.admin;
    });
  }, [recipientsList]);

  // get contacts that have no notifications set
  const noNotificationsSetContacts = useMemo(() => {
    return recipientsList.filter(item => {
      return !item.notifications;
    });
  }, [recipientsList]);

  const filterItems = useCallback(
    value => {
      setContactFilters(value);

      let newArray = [];
      switch (value) {
        case 'NON_ADMIN':
          newArray = recipientsList.filter(item => !item.admin);
          setRecipientsListFilter(newArray);
          break;

        case 'ADMIN':
          newArray = recipientsList.filter(item => item.admin);
          setRecipientsListFilter(newArray);
          break;

        case 'NO_NOTIFICATION':
          newArray = recipientsList.filter(item => !item.notifications);
          setRecipientsListFilter(newArray);
          break;

        default:
          setRecipientsListFilter(recipientsList);
      }
    },
    [recipientsList]
  );

  const filterChoices = useMemo(
    () =>
      notificationMethod === 'REPORTING'
        ? filtersLookupsReporting
        : filtersLookupsEmail,
    [notificationMethod]
  );
  return (
    <Grid container justifyContent="center">
      <div className={classes.informationsContainer}>
        <Grid container alignItems="center">
          <Grid item className={classes.gridItem}>
            <span className={classes.informationText}>
              Batch ID:
              <span className={classes.informationNumber}>
                {resourceUploadId}
              </span>
            </span>
          </Grid>
          {!sendHardCopy && (
            <Grid item className={classes.gridItem}>
              <span className={classes.informationText}>
                Total number of Contacts:
                <span className={classes.informationNumber}>
                  {recipientsList.length}
                </span>
              </span>
            </Grid>
          )}
          {sendHardCopy && (
            <Grid item className={classes.gridItem}>
              <span className={classes.informationText}>
                Total number of Investors:
                <span className={classes.informationNumber}>
                  {recipientsList.length}
                </span>
              </span>
            </Grid>
          )}
          {notificationMethod !== 'MAIL' && (
            <Grid item className={classes.gridItem}>
              <span className={classes.informationText}>
                Distinct Contacts:
                <span className={classes.informationNumber}>
                  {uniqBy(recipientsList, 'userId').length}
                </span>
              </span>
            </Grid>
          )}
          {notificationMethod !== 'EMAIL' && !sendHardCopy && (
            <>
              <Grid item className={classes.gridItem}>
                <span className={classes.informationText}>
                  Non-admin Contacts:
                  <span className={classes.informationNumber}>
                    {nonAdminContacts.length}
                  </span>
                </span>
              </Grid>
              <Grid item className={classes.gridItem}>
                <span className={classes.informationText}>
                  Admin Contacts:
                  <span className={classes.informationNumber}>
                    {adminContacts.length}
                  </span>
                </span>
              </Grid>
            </>
          )}
          {notificationMethod === 'REPORTING' && (
            <Grid item className={classes.gridItem}>
              <span className={classes.informationText}>
                No Notifications Set:
                <span className={classes.informationNumber}>
                  {noNotificationsSetContacts.length}
                </span>
              </span>
            </Grid>
          )}
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="flex-end"
          className={classes.selectInputContainer}
        >
          {notificationMethod === 'REPORTING' ? (
            <div className={classes.selectInputText}>
              Show:
              <span className={classes.informationNumber}>
                <CustomSimpleForm
                  toolbar={null}
                  initialValues={{ selectedFilter: 'ALL' }}
                >
                  <SelectInput
                    label={false}
                    source={`selectedFilter`}
                    choices={filterChoices}
                    variant="standard"
                    onChange={event => filterItems(event.target.value)}
                    helperText={false}
                  />
                </CustomSimpleForm>
              </span>
            </div>
          ) : (
            <span className={classes.nonFilter}></span>
          )}
        </Grid>
      </div>

      <RecipientsTable
        classes={classes}
        recipientsListFiltered={recipientsListFiltered}
        handleOpenEmailModal={handleOpenEmailModal}
        notificationMethod={notificationMethod}
        handleDownloadAsExcel={handleDownloadAsExcel}
        isLoadingExcelDoc={isLoadingExcelDoc}
        hasExportPermission={hasExportPermission}
        resourceUploadId={resourceUploadId}
        contactFilters={contactFilters}
        sendHardCopy={sendHardCopy}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        numSelectedRows={numSelectedRows}
        setNumSelectedRows={setNumSelectedRows}
      />
    </Grid>
  );
};

const RecipientsTable = ({
  classes,
  recipientsListFiltered,
  handleOpenEmailModal,
  notificationMethod,
  handleDownloadAsExcel,
  isLoadingExcelDoc,
  hasExportPermission,
  resourceUploadId,
  contactFilters,
  sendHardCopy,
  selectedRows,
  setSelectedRows,
  numSelectedRows,
  setNumSelectedRows
}) => {
  //NOSONAR javascript:S3776
  const tableHeadCells = [];

  if (sendHardCopy) {
    tableHeadCells.push({ id: 'Investor' });
    tableHeadCells.push({ id: 'Address' });
    tableHeadCells.push({ id: 'Document' });
  } else {
    tableHeadCells.push({ id: 'Fund' });
    tableHeadCells.push({ id: 'Investor' });
    tableHeadCells.push({ id: 'Contact' });
    tableHeadCells.push({ id: 'Email' });

    if (notificationMethod === 'EMAIL') {
      tableHeadCells.push({ id: 'Document' });
    }
    if (notificationMethod === 'MAIL') {
      tableHeadCells.push({ id: 'Address' });
      tableHeadCells.push({ id: 'Document' });
    }
  }
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();

  /**
   * Calculating number of rows depending of the filter
   */
  useEffect(() => {
    if (sendHardCopy) {
      const tempArray = recipientsListFiltered.filter(item =>
        selectedRows.some(
          row =>
            row.userId === item.userId &&
            (row.fundId || '') === (item.fundId || '') &&
            (row.investorId || '') === (item.investorId || '')
        )
      );
      setNumSelectedRows(tempArray.length);
    } else {
      const newArray = recipientsListFiltered.filter(
        item =>
          !item.dailySummary &&
          selectedRows.some(
            row =>
              row.userId === item.userId &&
              (row.fundId || '') === (item.fundId || '') &&
              (row.investorId || '') === (item.investorId || '')
          )
      );
      setNumSelectedRows(newArray.length);
    }
  }, [recipientsListFiltered, selectedRows, sendHardCopy, setNumSelectedRows]);

  const rowCount = useMemo(() => {
    if (sendHardCopy) {
      return recipientsListFiltered.length;
    }
    return filter(recipientsListFiltered, ['dailySummary', false]).length;
  }, [recipientsListFiltered, sendHardCopy]);

  /**
   * When we click the global checkbox
   */
  const handleClickAll = useCallback(() => {
    if (sendHardCopy) {
      handleSelectAllInvestorRecipients(
        numSelectedRows,
        rowCount,
        recipientsListFiltered,
        selectedRows,
        setSelectedRows
      );
    } else {
      handleSelectAllUserRecipients(
        numSelectedRows,
        rowCount,
        recipientsListFiltered,
        selectedRows,
        setSelectedRows
      );
    }
  }, [
    numSelectedRows,
    recipientsListFiltered,
    rowCount,
    selectedRows,
    sendHardCopy,
    setSelectedRows
  ]);

  /**
   * When we click on the checkbox in a row
   * @param {*} checked
   * @param {*} item
   */
  const handleRowSelect = useCallback(
    (checked, item) => {
      setSelectedRows(prevSelectedRows => {
        if (checked) {
          return uniqBy(
            [...prevSelectedRows, item],
            row => `${row.userId}-${row.fundId}-${row.investorName}`
          );
        } else {
          return prevSelectedRows.filter(
            itemRow =>
              itemRow.userId !== item.userId ||
              itemRow.fundId !== item.fundId ||
              itemRow.investorId !== item.investorId
          );
        }
      });
    },
    [setSelectedRows]
  );

  const downloadAttachment = useCallback(
    async ({ documentId, documentFileName }) => {
      try {
        const response = await DocumentApi.download(documentId);
        saveAs(response.data, documentFileName);
      } catch (error) {
        notify(getIcError(error), { type: 'error' });
      }
    },
    [notify]
  );

  const tooltipTitle = 'Contact will receive Daily Document Summary Email.';

  const [openConfirmPostalMail, setOpenConfirmPostalMail] = useState(false);

  const sendPostalMail = useCallback(() => {
    const groupsDict = {};
    var sendingList;

    if (!sendHardCopy) {
      sendingList = selectedRows.reduce((acc, curr) => {
        const groupId = `${curr.documentId}:${curr.fundId}:${curr.investorId}`;
        let group = groupsDict[groupId];

        if (group === undefined) {
          group = {
            documentId: curr.documentId,
            documentFileName: curr.documentFileName,
            documentTitle: curr.documentTitle,
            documentDate: curr.documentDate,
            fundId: curr.fundId,
            investorId: curr.investorId,
            categoryId: curr.categoryId,
            users: [
              {
                userId: curr.userId,
                email: curr.email,
                firstname: curr.firstname,
                lastname: curr.lastname,
                fax: curr.fax,
                company: curr.company,
                address: curr.address,
                dailySummary: false,
                notifications: false,
                admin: false
              }
            ],
            investors: []
          };

          groupsDict[groupId] = group;
          acc.push(group);
        } else {
          group.users.push({
            userId: curr.userId,
            email: curr.email,
            firstname: curr.firstname,
            lastname: curr.lastname,
            fax: curr.fax,
            company: curr.company,
            address: curr.address,
            dailySummary: false,
            notifications: false,
            admin: false
          });
        }
        return acc;
      }, []);
    } else {
      sendingList = selectedRows.reduce((acc, curr) => {
        const groupId = `${curr.documentId}:${curr.fundId}:${curr.investorId}`;
        let group = groupsDict[groupId];

        if (group === undefined) {
          group = {
            documentId: curr.documentId,
            documentFileName: curr.documentFileName,
            documentTitle: curr.documentTitle,
            documentDate: curr.documentDate,
            fundId: curr.fundId,
            investorId: curr.investorId,
            categoryId: curr.categoryId,
            users: [],
            investors: [
              {
                investorId: curr.investorId,
                investorName: curr.investorName,
                addressLine1: curr.addressLine1,
                addressLine2: curr.addressLine2,
                addressLine3: curr.addressLine3,
                addressCity: curr.addressCity,
                addressCountry: curr.addressCountry,
                addressStateProvince: curr.addressStateProvince,
                addressZipCode: curr.addressZipCode
              }
            ]
          };

          groupsDict[groupId] = group;
          acc.push(group);
        } else {
          group.investors.push({
            investorId: curr.investorId,
            investorName: curr.investorName,
            addressLine1: curr.addressLine1,
            addressLine2: curr.addressLine2,
            addressLine3: curr.addressLine3,
            addressCity: curr.addressCity,
            addressCountry: curr.addressCountry,
            addressStateProvince: curr.addressStateProvince,
            addressZipCode: curr.addressZipCode
          });
        }
        return acc;
      }, []);
    }

    const postalMailObject = {
      resourceUploadId,
      to: sendingList
    };

    PostalMailApi.sendPostalMail(postalMailObject)
      .then(() => {
        setOpenConfirmPostalMail(false);
        notify('ra.notification.send_envelopes', {
          messageArgs: { smart_count: selectedRows.length }
        });
      })
      .catch(notifyServerError);
  }, [notify, resourceUploadId, selectedRows, sendHardCopy, notifyServerError]);
  return (
    <>
      <Paper className={classes.paperContainer}>
        <div className={classes.table}>
          <Table
            stickyHeader
            aria-label="sticky table"
            size="small"
            className={classes.tableContainer}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" className={classes.checkboxCell}>
                  <Checkbox
                    indeterminate={
                      numSelectedRows > 0 && numSelectedRows < rowCount
                    }
                    checked={numSelectedRows === rowCount && rowCount > 0}
                    onClick={handleClickAll}
                    className={classes.checkbox}
                    disabled={
                      sendHardCopy
                        ? false
                        : find(recipientsListFiltered, [
                            'dailySummary',
                            false
                          ]) === undefined
                    }
                  />
                </TableCell>
                {tableHeadCells.map(headCell => (
                  <TableCell className={classes.cell} key={headCell.id}>
                    {headCell.id}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {!sendHardCopy && (
              <TableBody>
                {recipientsListFiltered.map((item, index) => {
                  return (
                    <TableRow key={index} hover>
                      <Tooltip
                        placement="top-end"
                        disableHoverListener={!item.dailySummary}
                        classes={{
                          tooltipPlacementTop: classes.tooltipPlacementTop
                        }}
                        title={tooltipTitle}
                      >
                        <TableCell
                          padding="checkbox"
                          className={classes.checkboxCell}
                        >
                          <Checkbox
                            onClick={event =>
                              handleRowSelect(event.target.checked, item)
                            }
                            className={item.checkbox}
                            checked={
                              item.dailySummary
                                ? false
                                : selectedRows.some(
                                    row =>
                                      row.userId === item.userId &&
                                      (row.fundId || '') ===
                                        (item.fundId || '') &&
                                      (row.investorId || '') ===
                                        (item.investorId || '')
                                  )
                            }
                            disabled={item.dailySummary}
                            id={item.userId + ' checkbox'}
                          />
                        </TableCell>
                      </Tooltip>
                      <TableCell id={item.userId + ' fundName'}>
                        <div style={{ display: 'flex' }}>
                          {item.fundStatus === 'PENDING' && (
                            <PendingIndicatorIcon title="Pending Fund" />
                          )}
                          {item.fundName}
                        </div>
                      </TableCell>
                      <TableCell id={item.userId + ' investorName'}>
                        <div style={{ display: 'flex' }}>
                          {item.investorStatus === 'INACTIVE' && (
                            <PendingIndicatorIcon title="Inactive Investor" />
                          )}
                          {item.investorName}
                        </div>
                      </TableCell>
                      <TableCell
                        id={item.userId + ' firstname'}
                      >{`${item.firstname} ${item.lastname}`}</TableCell>
                      <TableCell id={item.userId + ' email'}>
                        {item.email}
                      </TableCell>
                      {notificationMethod === 'EMAIL' && (
                        <TableCell id={item.userId + ' documentFileName'}>
                          <span
                            className={classes.downloadDoc}
                            onClick={() => downloadAttachment(item)}
                          >
                            {item.documentFileName}
                          </span>
                        </TableCell>
                      )}
                      {notificationMethod === 'MAIL' && (
                        <>
                          <TableCell id={item.userId + ' address'}>
                            {`${item.address.addressLine1 || ''} ${item.address
                              .addressLine2 || ''} ${item.address
                              .addressLine3 || ''} ${item.address.city ||
                              ''} ${item.address.country || ''} ${item.address
                              .postalCode || ''} ${item.address.state || ''}`}
                          </TableCell>
                          <TableCell id={item.userId + ' documentFileName'}>
                            <span
                              className={classes.downloadDoc}
                              onClick={() => downloadAttachment(item)}
                            >
                              {item.documentFileName}
                            </span>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
            {sendHardCopy && (
              <TableBody>
                {recipientsListFiltered.map((item, index) => {
                  return (
                    <TableRow key={index} hover>
                      <Tooltip
                        placement="top-end"
                        disableHoverListener={!item.dailySummary}
                        classes={{
                          tooltipPlacementTop: classes.tooltipPlacementTop
                        }}
                        title={tooltipTitle}
                      >
                        <TableCell
                          padding="checkbox"
                          className={classes.checkboxCell}
                        >
                          <Checkbox
                            onClick={event =>
                              handleRowSelect(event.target.checked, item)
                            }
                            className={item.checkbox}
                            checked={
                              item.dailySummary
                                ? false
                                : selectedRows.some(
                                    row =>
                                      row.userId === item.userId &&
                                      (row.fundId || '') ===
                                        (item.fundId || '') &&
                                      (row.investorId || '') ===
                                        (item.investorId || '')
                                  )
                            }
                            disabled={item.dailySummary}
                            id={item.userId + ' checkbox'}
                          />
                        </TableCell>
                      </Tooltip>
                      <TableCell id={item.investorId + ' investorName'}>
                        <div style={{ display: 'flex' }}>
                          {item.investorName}
                        </div>
                      </TableCell>
                      <TableCell id={item.investorId + ' address'}>
                        {`${item.addressLine1 || ''} ${item.addressLine2 ||
                          ''} ${item.addressLine3 || ''} ${item.addressCity ||
                          ''} ${item.addressCountry ||
                          ''} ${item.addressZipCode ||
                          ''} ${item.addressStateProvince || ''}`}
                      </TableCell>
                      <TableCell id={item.investorId + ' documentFileName'}>
                        <span
                          className={classes.downloadDoc}
                          onClick={() => downloadAttachment(item)}
                        >
                          {item.documentFileName}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </div>
        {/* Button to edit Email if needed */}
        <div className={classes.buttonFooter}>
          {notificationMethod !== 'MAIL' && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleOpenEmailModal(selectedRows)}
              classes={{
                disabled: classes.buttonDisabled,
                outlined: classes.footerButton
              }}
              disabled={selectedRows.length < 1}
              disableFocusRipple
            >
              Edit Email
            </Button>
          )}

          {notificationMethod === 'MAIL' && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenConfirmPostalMail(true)}
              classes={{
                disabled: classes.buttonDisabled,
                outlined: classes.footerButton
              }}
              disabled={selectedRows.length < 1}
              disableFocusRipple
              startIcon={<MailIcon />}
            >
              Submit Letters To Be Mailed ({selectedRows.length})
            </Button>
          )}

          {hasExportPermission && (
            <SaveButton
              saving={isLoadingExcelDoc}
              handleSubmitWithRedirect={() =>
                handleDownloadAsExcel(contactFilters)
              }
              color="primary"
              disabled={isLoadingExcelDoc}
              variant="outlined"
              className={classes.footerButton}
              label={'Download as Excel'}
              icon={<></>}
            />
          )}
        </div>
      </Paper>
      <Confirm
        isOpen={openConfirmPostalMail}
        title="Postal Mail Confirmation"
        content={`Confirm Send ${selectedRows.length} ${
          selectedRows.length > 1 ? 'Envelopes' : 'Envelope'
        }`}
        onConfirm={sendPostalMail}
        onClose={() => setOpenConfirmPostalMail(false)}
        confirm="ra.action.confirm"
      />
    </>
  );
};

/**
 * creating an array of ids
 * @param {*} recipientsList
 */
const getFundAndInvestorIds = (recipientsList = []) => {
  let fundIds = [];
  let investorIds = [];

  recipientsList.forEach(row => {
    if (row && row.fundId && row.fundId.split('|').length === 1) {
      // Only one fund, otherwise Multiple funds for admin
      fundIds.push(`${row.fundId}`);
    }
  });
  recipientsList.forEach(row => {
    if (row && row.investorId && row.investorId !== '*') {
      investorIds.push(`${row.investorId}`);
    }
  });

  return { fundIds: uniq(fundIds), investorIds: uniq(investorIds) };
};

/**
 * formatting Data once
 * @param {*} data
 */
const formatData = (data = []) => {
  return data.reduce((acc, curr) => {
    var newData;
    if (
      data &&
      data[0] &&
      data[0].investors &&
      data[0].investors.length === 0
    ) {
      newData = curr.users.map(user => {
        return {
          ...user,
          fundId: curr.fundId,
          investorId: curr.investorId,
          documentId: curr.documentId,
          documentFileName: curr.documentFileName,
          documentTitle: curr.documentTitle,
          documentDate: curr.documentDate,
          categoryId: curr.categoryId
        };
      });
    } else if (
      data &&
      data[0] &&
      data[0].investors &&
      data[0].investors.length !== 0
    ) {
      newData = curr.investors.map(investor => {
        return {
          ...investor,
          investorId: investor.investorId,
          documentId: curr.documentId,
          documentFileName: curr.documentFileName,
          documentTitle: curr.documentTitle,
          documentDate: curr.documentDate
        };
      });
    }
    return acc.concat(newData);
  }, []);
};

const isSendHardCopy = data => {
  return data && data[0] && data[0].investors && data[0].investors.length !== 0;
};

/**
 * creating the final list to be display
 * @param {*} recipientsList
 * @param {*} fundsResponse
 * @param {*} investorsResponse
 * @param categoriesResponse
 */
const getFinalList = (
  recipientsList,
  fundsResponse,
  investorsResponse,
  categoriesResponse
) => {
  return recipientsList.reduce((acc, curr) => {
    // Matching investorId with what we have coming back from the api call on investors (to getting name)
    let contactWithInvestorName =
      investorsResponse.find(investor => curr.investorId === investor.id) || {};
    if (curr.investorId === '' || curr.investorId === '*') {
      contactWithInvestorName = { name: 'All Investors' };
    }

    // Matching fundId with what we have coming back from the api call on funds (to getting name)
    let contactWithFundName;
    if (fundsResponse.length > 0) {
      contactWithFundName =
        fundsResponse.find(fund => curr.fundId === fund.id) || {};
      if (curr.fundId.split('|').length > 1) {
        contactWithFundName = { name: 'Multiple funds' };
      }
    }

    // Matching categoryId with what we have coming back from the api call on categories (to getting name)
    let contactWithCategoryName =
      categoriesResponse.find(category => curr.categoryId === category.id) ||
      '';

    // Adding the name of fund and investor and categoryName for every initial object from the api
    curr = {
      ...curr,
      fundName: contactWithFundName ? contactWithFundName.name : '',
      fundStatus: contactWithFundName ? contactWithFundName.status : '',
      investorName: contactWithInvestorName ? contactWithInvestorName.name : '',
      investorStatus: contactWithInvestorName
        ? contactWithInvestorName.status
        : '',
      categoryName: contactWithCategoryName ? contactWithCategoryName.name : ''
    };
    acc.push(curr);
    return acc;
  }, []);
};

const useFundsAndInvestorsData = recipientsList => {
  const { fundIds, investorIds } = getFundAndInvestorIds(recipientsList);

  const idsToSend =
    investorIds.length > limitToCall ? [] : `[${Array.from(investorIds)}]`;
  const {
    data: investorsResponse,
    loading: investorsLoading,
    error: investorsError
  } = useQuery({
    type: 'getManyReference',
    resource: 'investors',
    payload: {
      id: idsToSend,
      target: 'id'
    }
  });
  const {
    data: fundsResponse,
    loading: fundsLoading,
    error: fundsError
  } = useQuery({
    type: 'getMany',
    resource: 'funds',
    payload: { ids: Array.from(fundIds) }
  });

  return {
    fundsResponse,
    investorsResponse,
    fundsLoading,
    investorsLoading,
    fundsError,
    investorsError
  };
};

const useCategoryHook = recipientsList => {
  let categoryIds = [];

  if (recipientsList !== undefined) {
    recipientsList.forEach(row => {
      if (
        row &&
        row.categoryId !== 0 &&
        !categoryIds.includes(row.categoryId)
      ) {
        categoryIds.push(row.categoryId);
      }
    });
  }

  const idsToSend =
    categoryIds.length > limitToCall ? [] : `[${Array.from(categoryIds)}]`;
  const {
    data: categoriesResponse,
    loading: categoriesLoading,
    error: categoriesError
  } = useQuery({
    type: 'getManyReference',
    resource: 'categories',
    payload: {
      id: idsToSend,
      target: 'id'
    }
  });

  return {
    categoriesResponse,
    categoriesLoading,
    categoriesError
  };
};

function handleSelectAllUserRecipients(
  numSelectedRows,
  rowCount,
  recipientsListFiltered,
  selectedRows,
  setSelectedRows
) {
  if (numSelectedRows === rowCount) {
    let newArray = [];
    recipientsListFiltered.forEach(recipient => {
      if (recipient.dailySummary === false) {
        if (
          selectedRows.some(
            row =>
              row.userId === recipient.userId &&
              (row.fundId || '') === (recipient.fundId || '') &&
              (row.investorId || '') === (recipient.investorId || '')
          )
        ) {
          newArray.push(recipient);
        }
      }
    });
    const fieldsToCompare = ['userId', 'fundId', 'investorId'];
    setSelectedRows(
      uniqBy(
        differenceByFields(selectedRows, newArray, fieldsToCompare),
        user => `${user.userId}-${user.fundId}-${user.investorName}`
      )
    );
  } else if (numSelectedRows !== rowCount) {
    setSelectedRows(
      uniqBy(
        selectedRows.concat(
          filter(recipientsListFiltered, ['dailySummary', false])
        ),
        user => `${user.userId}-${user.fundId}-${user.investorName}`
      )
    );
  }
}

function handleSelectAllInvestorRecipients(
  numSelectedRows,
  rowCount,
  recipientsListFiltered,
  selectedRows,
  setSelectedRows
) {
  if (numSelectedRows === rowCount) {
    let newArray = [];
    recipientsListFiltered.forEach(recipient => {
      if (selectedRows.includes(recipient)) {
        newArray.push(recipient);
      }
    });
    setSelectedRows(difference(selectedRows, newArray));
  } else if (numSelectedRows !== rowCount) {
    setSelectedRows(selectedRows.concat(recipientsListFiltered));
  }
}

const CommonTab = ({
  resourceupload: { resourceUploadId },
  classes,
  notificationMethod,
  ...props
}) => {
  const includeAdmin = notificationMethod === 'EMAIL' ? 'false' : 'true';

  const { loaded, error, data } = useQueryWithStore({
    type: 'GET_MANY_REFERENCE',
    resource: 'distributionlist',
    payload: {
      target: 'resourceUploadId',
      id: resourceUploadId,
      filter: {
        notificationMethod,
        includeAdmin: includeAdmin,
        includeCC: notificationMethod !== 'MAIL'
      }
    }
  });

  const sendHardCopy = isSendHardCopy(data);
  const recipientsList = data && formatData(data);
  const {
    fundsResponse,
    investorsResponse,
    fundsLoading,
    investorsLoading,
    fundsError,
    investorsError
  } = useFundsAndInvestorsData(recipientsList);

  const {
    categoriesResponse,
    categoriesLoading,
    categoriesError
  } = useCategoryHook(recipientsList);
  const notify = useNotify();

  const [isLoadingExcelDoc, setLoadingExcelDoc] = useState(false);
  /**
   * Click on button 'Download as Excel' to download the excel file
   */
  const handleDownloadAsExcel = useCallback(
    contactFilters => {
      setLoadingExcelDoc(true);

      ResourceUploadApi.getResourceUploadDistributionListExcel({
        resourceUploadId,
        notificationMethod,
        contactFilter: contactFilters
      })
        .then(resp => {
          const fileName = 'delivery.xlsx';
          saveAs(resp, fileName);
          setLoadingExcelDoc(false);
        })
        .catch(() => {
          setLoadingExcelDoc(false);
          notify('ra.page.error', { type: 'error' });
        });
    },
    [notify, resourceUploadId, notificationMethod]
  );

  if (!loaded || fundsLoading || investorsLoading || categoriesLoading)
    return <Loading className={classes.loading} />;
  if (error || fundsError || investorsError || categoriesError)
    return <Error />;

  if (
    (!isEmpty(fundsResponse) || sendHardCopy) &&
    !isEmpty(investorsResponse)
  ) {
    const finalList = getFinalList(
      recipientsList,
      fundsResponse,
      investorsResponse,
      categoriesResponse
    );

    const handleOpenEmailModal = itemsSelected => {
      const { openEmailModal } = props;
      openEmailModal(
        itemsSelected,
        filter(finalList, ['dailySummary', false]),
        notificationMethod
      );
    };

    return (
      <DeliveryTable
        recipientsList={finalList}
        sendHardCopy={sendHardCopy}
        classes={classes}
        resourceUploadId={resourceUploadId}
        handleOpenEmailModal={handleOpenEmailModal}
        notificationMethod={notificationMethod}
        handleDownloadAsExcel={handleDownloadAsExcel}
        isLoadingExcelDoc={isLoadingExcelDoc}
        hasExportPermission={
          props.permissions && props.permissions.includes('AUTH_EXPORT_EXCEL')
        }
      />
    );
  } else {
    return (
      <span>
        No data to be displayed for this batch ID : {resourceUploadId}
      </span>
    );
  }
};

function differenceByFields(arr1, arr2, fields) {
  return arr1.filter(
    item1 =>
      !arr2.some(item2 => fields.every(field => item1[field] === item2[field]))
  );
}

export default withStyles(CommonTabStyles)(CommonTab);
